import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * SignaturePad — HTML5 canvas + pointer events, exposes getDataURL/isEmpty/clear.
 * Same public API as v1; only visual chrome changed (dashed inner outline, glass frame).
 */
export const SignaturePad = forwardRef(function SignaturePad(
    { onEnd, initialDataUrl, disabled, "data-testid": dataTestId = "signature-pad" },
    ref
) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef(null);
    const dirtyRef = useRef(false);
    const [hasInk, setHasInk] = useState(false);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            const rect = c.getBoundingClientRect();
            c.width = Math.floor(rect.width * dpr);
            c.height = Math.floor(rect.height * dpr);
            const ctx = c.getContext("2d");
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.lineJoin = "round";
            ctx.lineCap = "round";
            ctx.strokeStyle = "#0F172A";
            ctx.lineWidth = 2.2;
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, rect.width, rect.height);
        };
        resize();
        const obs = new ResizeObserver(resize);
        obs.observe(c);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (initialDataUrl) setFromDataURL(initialDataUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDataUrl]);

    const point = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onPointerDown = (e) => {
        if (disabled) return;
        canvasRef.current.setPointerCapture(e.pointerId);
        drawingRef.current = true;
        lastRef.current = point(e);
    };

    const onPointerMove = (e) => {
        if (!drawingRef.current) return;
        const cur = point(e);
        const last = lastRef.current;
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(cur.x, cur.y);
        ctx.stroke();
        lastRef.current = cur;
        dirtyRef.current = true;
        if (!hasInk) setHasInk(true);
    };

    const onPointerUp = (e) => {
        if (!drawingRef.current) return;
        try {
            canvasRef.current.releasePointerCapture(e.pointerId);
        } catch {}
        drawingRef.current = false;
        lastRef.current = null;
        if (dirtyRef.current && onEnd) {
            dirtyRef.current = false;
            const dataUrl = canvasRef.current.toDataURL("image/png");
            onEnd(dataUrl);
        }
    };

    const clear = () => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext("2d");
        const rect = c.getBoundingClientRect();
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.restore();
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
        setHasInk(false);
        if (onEnd) onEnd("");
    };

    const setFromDataURL = (dataUrl) => {
        const c = canvasRef.current;
        if (!c || !dataUrl) return;
        const img = new Image();
        img.onload = () => {
            const rect = c.getBoundingClientRect();
            const ctx = c.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, rect.width, rect.height);
            ctx.drawImage(img, 0, 0, rect.width, rect.height);
            setHasInk(true);
        };
        img.src = dataUrl.startsWith("data:") ? dataUrl : `data:image/png;base64,${dataUrl}`;
    };

    useImperativeHandle(
        ref,
        () => ({
            getDataURL: () => (hasInk ? canvasRef.current?.toDataURL("image/png") : null),
            isEmpty: () => !hasInk,
            clear,
            setFromDataURL,
        }),
        [hasInk]
    );

    return (
        <div className="w-full" data-testid={dataTestId}>
            <div className="label">Signature</div>
            <canvas
                ref={canvasRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                className={`sig-canvas h-[140px] ${
                    disabled ? "opacity-60 cursor-not-allowed" : ""
                }`}
                data-testid={`${dataTestId}-canvas`}
                aria-label="Signature canvas"
            />
            <div className="flex items-center justify-between mt-2">
                <span
                    className="text-[12px] text-slate-500"
                    data-testid={`${dataTestId}-hint`}
                >
                    Sign with mouse, pen, or finger.
                </span>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={clear}
                    disabled={disabled}
                    data-testid={`${dataTestId}-clear`}
                >
                    <Eraser size={13} strokeWidth={2} />
                    Clear
                </button>
            </div>
        </div>
    );
});

export default SignaturePad;
